import type { Cluster } from '@googlemaps/markerclusterer';

import {
  clusterMarkerFormattersHTML,
  MarkerType,
} from '@apps/main/modules/maps/markerFormatters';

import type {
  DestinationStaticInfo,
  ContentApiImage,
  IngredientId,
  OverviewDestinationStaticInfo,
} from '@libs/trip-planner-interfaces';
import type { ClusterOptions, Marker } from '@libs/maps';

import type { HotelMapCardData, HighlightedHotel } from '../../models';

const HOTEL_SELECTION_MARKERS = [MarkerType.Hotel] as string[];

export function getHotelSelectionMarkers(
  markers: Marker[],
  highlightedHotel: HighlightedHotel | undefined,
  topProducts?: string[]
): Marker[] {
  return markers
    .filter(
      (marker) =>
        marker.useContentFormatter &&
        HOTEL_SELECTION_MARKERS.includes(marker.useContentFormatter)
    )
    .map((marker) => {
      const isTopMarker = topProducts?.includes(marker.data.hotelCode);

      const clusteredMarker = {
        ...marker,
        cluster: !isTopMarker,
      };

      if (highlightedHotel && marker.key.includes(highlightedHotel.hotelCode)) {
        const baseCss = marker.cssClass ?? '';
        const cssClass = `${baseCss} highlighted`;
        return {
          ...clusteredMarker,
          cssClass,
          cluster: false,
          displayPriority: 4,
        };
      }

      return clusteredMarker;
    });
}

export function getClusterOptions(): ClusterOptions {
  return {
    minPoints: 10,
    radius: 120,
  };
}

export function getClusterHotelMarkerFormatter(): Record<
  MarkerType.Hotel,
  (marker: Marker, cluster: Cluster) => HTMLDivElement
> {
  return {
    [MarkerType.Hotel]: clusterMarkerFormattersHTML[MarkerType.Hotel],
  };
}

export function getMarkerFormatters(): Record<
  MarkerType.Hotel,
  (marker: Marker) => HTMLDivElement
> {
  return {
    [MarkerType.Hotel]: (marker): HTMLDivElement => {
      const div = document.createElement('div');

      div.className = `hotel-selection-accommodation-marker ${
        marker.cssClass || ''
      }`;

      div.innerHTML = `
      <div class="hotel-selection-map-marker-label">
        <span>${marker.data?.formattedPrice}</span>
      </div>
    `;

      return div;
    },
  };
}

export function getHotelMapCardData(
  hotelMapCard: HighlightedHotel | undefined,
  getStaticInfo: (
    hotelCode: string,
    ingredientId: IngredientId
  ) => DestinationStaticInfo | OverviewDestinationStaticInfo | undefined,
  getImage: (
    image: ContentApiImage,
    hotelCode?: string,
    width?: number,
    height?: number
  ) => string
): HotelMapCardData | undefined {
  if (!hotelMapCard) {
    return;
  }

  const { ingredientId, hotelCode } = hotelMapCard;
  const staticInfo = getStaticInfo(hotelCode, ingredientId);

  if (!staticInfo || !staticInfo.images || !staticInfo.images[0]) {
    return;
  }

  return {
    ingredientId: ingredientId,
    ingredientIndex: hotelMapCard.ingredientIndex,
    hotelCode: hotelCode,
    hotelName: staticInfo.name,
    starsRating: staticInfo.semboSunRating,
    imageUrl: getImage(staticInfo.images[0]),
    accommodationType: staticInfo.typeOfHotel,
  };
}

export function getHighlightedHotel(
  hotelCode: string | undefined,
  ingredientId: IngredientId | undefined,
  ingredientIndex: number
): HighlightedHotel | undefined {
  if (hotelCode && ingredientId) {
    return {
      hotelCode,
      ingredientId,
      ingredientIndex,
      showHotelMapCard: false,
    };
  } else {
    return undefined;
  }
}
